import React, { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { sanitizeQueryParam } from '@v2/lib';
import {
  PartnersTable,
  PartnerAccountPanel,
  PartnerOfferingPanel,
  PartnerDomainPanel,
} from './components';
import { Partner, PartnerOfferingWithBitwerx } from '@v2/types';
import {
  useBitwerxOfferings,
  useDeleteDomain,
  usePartnerDetails,
  usePartners,
  useTableFilters,
} from '@v2/hooks';
import { Button, CollapsiblePanel, DetailField, detailFieldStyles } from '@v2/components';
import type { ActionMenuItem } from '@v2/components';
import { SettingsPageLayout, DetailPanel, ListDetailView } from '@v2/layouts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faPlus } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { parsePartnerOfferings } from './utils';
import {
  CreateDomainForm,
  CreatePartnerForm,
  ManagePartnerAccounts,
  OfferingForm,
} from '@v2/features';
import { styles } from './styles';

const PartnersPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [isAddDomainModalOpen, setIsAddDomainModalOpen] = useState(false);
  const [isCreatePartnerModalOpen, setIsCreatePartnerModalOpen] = useState(false);
  const [isManagePartnerAccountsModalOpen, setIsManagePartnerAccountsModalOpen] = useState(false);
  const [isManagePartnerOfferingModalOpen, setIsManagePartnerOfferingModalOpen] = useState(false);
  const [selectedPartnerOffering, setSelectedPartnerOffering] =
    useState<PartnerOfferingWithBitwerx | null>(null);
  const searchParams = new URLSearchParams(location.search);
  const selectedPartnerId = sanitizeQueryParam(searchParams.get('partnerId'));

  const {
    partners,
    loading: partnersLoading,
    error: partnersError,
    refetch: refetchPartners,
  } = usePartners();

  const { filteredData, search, setSearch, sortConfig, handleSort } = useTableFilters({
    data: partners,
    searchKeys: ['name'],
  });

  const {
    partner,
    loading: detailsLoading,
    error: detailsError,
    refetch: refetchDetails,
  } = usePartnerDetails(selectedPartnerId);

  const { offerings: bitwerxOfferings } = useBitwerxOfferings();

  const { deleteDomain } = useDeleteDomain();

  const detailActions: ActionMenuItem[] | undefined = useMemo(() => {
    if (!partner) return undefined;
    return [
      {
        key: 'manage-accounts',
        label: 'Manage accounts',
        icon: <FontAwesomeIcon icon={faUsers} />,
        onClick: () => setIsManagePartnerAccountsModalOpen(true),
      },
    ];
  }, [partner]);

  const handleSelectPartner = (partner: Partner) => {
    searchParams.set('partnerId', partner.id);
    history.push({ search: searchParams.toString() });
  };

  const handleClosePanel = () => {
    searchParams.delete('partnerId');
    history.push({ search: searchParams.toString() });
  };

  const handleCreatePartner = () => {
    setIsCreatePartnerModalOpen(true);
  };

  const onCompletedCreatePartner = () => {
    setIsCreatePartnerModalOpen(false);
    refetchPartners();
  };

  const handleDeleteDomain = async (domainId: string) => {
    const success = await deleteDomain(domainId);
    if (success) {
      toast.success('Domain deleted successfully');
      refetchDetails();
    } else {
      toast.error('Failed to delete domain');
    }
  };

  const onCompletedAddDomain = () => {
    setIsAddDomainModalOpen(false);
    refetchDetails();
  };

  const onCompletedAddAccount = () => {
    refetchDetails();
  };

  return (
    <>
      <SettingsPageLayout
        title="Partners"
        actions={
          <Button
            variant="primary"
            onClick={handleCreatePartner}
            icon={<FontAwesomeIcon icon={faPlus} />}>
            Create Partner
          </Button>
        }>
        <ListDetailView
          selectedId={selectedPartnerId}
          list={
            <PartnersTable
              partners={filteredData}
              loading={partnersLoading}
              error={partnersError}
              onSelectPartner={handleSelectPartner}
              selectedPartnerId={selectedPartnerId || undefined}
              sortConfig={sortConfig}
              onSortChange={handleSort}
              search={search}
              onSearchChange={setSearch}
              searchPlaceholder="Search by name..."
            />
          }
          detail={
            <DetailPanel
              title={partner?.name || 'Partner Details'}
              subtitle={partner?.id ? `(${partner.id})` : undefined}
              onClose={handleClosePanel}
              actions={detailActions}
              loading={detailsLoading}
              loadingMessage="Loading partner details..."
              error={detailsError}
              errorMessage="There was an error fetching the partner details."
              onRetry={refetchPartners}>
              {partner && (
                <div className={styles.detailContent}>
                  <CollapsiblePanel title="Partner Information" defaultExpanded>
                    <div className={detailFieldStyles.fieldContainer}>
                      <DetailField label="Name">{partner.name}</DetailField>
                      {partner.email && <DetailField label="Email">{partner.email}</DetailField>}
                      {partner.phone && <DetailField label="Phone">{partner.phone}</DetailField>}
                      {partner.website && (
                        <DetailField label="Website">{partner.website}</DetailField>
                      )}
                      {partner.address && (
                        <DetailField label="Address">{partner.address}</DetailField>
                      )}
                      {partner.city && <DetailField label="City">{partner.city}</DetailField>}
                      {partner.stateProvince && (
                        <DetailField label="State/Province">{partner.stateProvince}</DetailField>
                      )}
                      {partner.postalCode && (
                        <DetailField label="Postal Code">{partner.postalCode}</DetailField>
                      )}
                      {(partner.createdByFirstName || partner.createdByLastName) && (
                        <DetailField label="Created By">
                          {[partner.createdByFirstName, partner.createdByLastName]
                            .filter(Boolean)
                            .join(' ')}
                        </DetailField>
                      )}
                      {partner.createdDate && (
                        <DetailField label="Created Date">
                          {new Date(partner.createdDate).toLocaleDateString()}
                        </DetailField>
                      )}
                    </div>
                  </CollapsiblePanel>
                  <PartnerAccountPanel
                    accounts={partner.accounts}
                    partnerId={partner.id}
                    handleAddAccount={() => setIsManagePartnerAccountsModalOpen(true)}
                    onAccountRemoved={refetchDetails}
                  />
                  <PartnerOfferingPanel
                    offerings={parsePartnerOfferings(partner.partnerOfferings, bitwerxOfferings)}
                    partnerId={partner.id}
                    onEditOffering={offering => {
                      setSelectedPartnerOffering(offering);
                      setIsManagePartnerOfferingModalOpen(true);
                    }}
                    onAddOffering={() => {
                      setSelectedPartnerOffering(null);
                      setIsManagePartnerOfferingModalOpen(true);
                    }}
                  />
                  <PartnerDomainPanel
                    domains={partner.trustedDomains}
                    handleAddDomain={() => setIsAddDomainModalOpen(true)}
                    handleDeleteDomain={handleDeleteDomain}
                  />
                </div>
              )}
            </DetailPanel>
          }
        />
      </SettingsPageLayout>
      {isAddDomainModalOpen && (
        <CreateDomainForm
          onCancel={() => setIsAddDomainModalOpen(false)}
          onCompleted={onCompletedAddDomain}
        />
      )}
      {isCreatePartnerModalOpen && (
        <CreatePartnerForm
          onCancel={() => setIsCreatePartnerModalOpen(false)}
          onCompleted={onCompletedCreatePartner}
        />
      )}
      {isManagePartnerAccountsModalOpen && (
        <ManagePartnerAccounts
          onClose={() => setIsManagePartnerAccountsModalOpen(false)}
          onCompleted={onCompletedAddAccount}
        />
      )}
      {isManagePartnerOfferingModalOpen && selectedPartnerId && (
        <OfferingForm
          bitwerxOfferings={bitwerxOfferings}
          partnerId={selectedPartnerId}
          partnerOffering={selectedPartnerOffering}
          onClose={() => {
            setIsManagePartnerOfferingModalOpen(false);
            setSelectedPartnerOffering(null);
          }}
          onCompleted={() => {
            setIsManagePartnerOfferingModalOpen(false);
            setSelectedPartnerOffering(null);
            refetchDetails();
          }}
        />
      )}
    </>
  );
};

export default PartnersPage;
